/**
 * METABOLIC PILLARS & REGISTRY
 * The single source of truth for nutrient categorization, display names, and synonyms.
 * V8.5: SIMPLIFIED PILLARS & DUAL-LINE TYPOGRAPHY.
 */

export interface NutrientProfile {
    label: string;
    subLabel?: string;
    unit: string;
    aliases: string[];
}

/**
 * THE CANONICAL NUTRIENT REGISTRY
 * Defines display labels, sub-labels (aliases), and all known synonyms/short-codes.
 */
export const NUTRIENT_REGISTRY: Record<string, NutrientProfile> = {
    // PILLAR: ENERGY
    'creatine': { label: 'Creatine', unit: 'mg', aliases: ['creatine_monohydrate'] },
    'iodine': { label: 'Iodine', unit: 'mcg', aliases: ['i'] },
    'zinc': { label: 'Zinc', unit: 'mg', aliases: ['zn', 'zinc_citrate', 'zinc_optizinc'] },
    'vitamin_d': { label: 'Vitamin D', subLabel: 'Cholecalciferol', unit: 'IU', aliases: ['d', 'cholecalciferol'] },
    'tyrosine': { label: 'Tyrosine', unit: 'mg', aliases: ['l-tyrosine'] },
    'ashwagandha': { label: 'Ashwagandha', subLabel: 'KSM-66', unit: 'mg', aliases: ['ksm-66', 'withania_somnifera'] },
    'caffeine': { label: 'Caffeine', unit: 'mg', aliases: ['anhydrous_caffeine'] },
    
    // OTHER MINERALS
    'selenium': { label: 'Selenium', unit: 'mcg', aliases: ['se'] },
    'chromium': { label: 'Chromium', unit: 'mcg', aliases: ['cr'] },
    'manganese': { label: 'Manganese', unit: 'mg', aliases: ['mn'] },
    'copper': { label: 'Copper', unit: 'mg', aliases: ['cu'] },

    // B-VITAMINS (Aggressive Alias Catching)
    'vitamin_b1': { label: 'Vitamin B1', subLabel: 'Thiamin', unit: 'mg', aliases: ['b1', 'thiamine', 'tiamin', 'thiamin_(b1)'] },
    'vitamin_b2': { label: 'Vitamin B2', subLabel: 'Riboflavin', unit: 'mg', aliases: ['b2', 'riboflavin', 'riboflavin_(b2)'] },
    'vitamin_b3': { label: 'Vitamin B3', subLabel: 'Niacin', unit: 'mg', aliases: ['niacin', 'b3', 'nicotinic_acid', 'niacin_(b3)'] },
    'vitamin_b5': { label: 'Vitamin B5', subLabel: 'Pantothenic Acid', unit: 'mg', aliases: ['b5', 'pantothenic_acid', 'pantothenic_acid_(b5)'] },
    'vitamin_b6': { label: 'Vitamin B6', subLabel: 'Pyridoxine', unit: 'mg', aliases: ['b6', 'p-5-p', 'pyridoxine', 'vitamin_b6_(p-5-p)'] },
    'biotin': { label: 'Vitamin B7', subLabel: 'Biotin', unit: 'mcg', aliases: ['b7', 'vitamin_b7'] },
    'folate': { label: 'Vitamin B9', subLabel: 'Folate', unit: 'mcg', aliases: ['b9', 'folic_acid', 'vitamin_b9'] },
    'vitamin_b12': { label: 'Vitamin B12', subLabel: 'Cobalamin', unit: 'mcg', aliases: ['b12', 'cobalamin', 'methylcobalamin'] },

    // PILLAR: CNS
    'magnesium': { label: 'Magnesium', unit: 'mg', aliases: ['mg', 'magnesium_citrate', 'magnesium_malate'] },
    'potassium': { label: 'Potassium', unit: 'mg', aliases: ['k', 'potassium_chloride'] },
    'sodium': { label: 'Sodium', unit: 'mg', aliases: ['na', 'salt'] },
    'calcium': { label: 'Calcium', unit: 'mg', aliases: ['ca', 'calcium_citrate'] },
    'taurine': { label: 'Taurine', unit: 'mg', aliases: ['l-taurine'] },
    'melatonin': { label: 'Melatonin', unit: 'mg', aliases: [] },
    'boron': { label: 'Boron', unit: 'mg', aliases: [] },

    // PILLAR: VASCULAR
    'citrulline': { label: 'Citrulline', unit: 'mg', aliases: ['l-citrulline', 'citrulline_malate'] },
    'iron': { label: 'Iron', unit: 'mg', aliases: ['fe', 'ferrous', 'ferrum'] },
    'omega_3': { label: 'Omega 3', subLabel: 'EPA/DHA', unit: 'mg', aliases: ['epa', 'dha', 'fish_oil'] },
    'vitamin_e': { label: 'Vitamin E', subLabel: 'Tocopherol', unit: 'mg', aliases: ['e', 'tocopherol'] },
    'vitamin_k': { label: 'Vitamin K', subLabel: 'Menaquinone', unit: 'mcg', aliases: ['k', 'k2', 'mk-7', 'menaquinone'] },
    'vitamin_a': { label: 'Vitamin A', subLabel: 'Retinol', unit: 'mcg', aliases: ['a', 'retinol'] },

    // PILLAR: STRUCTURAL
    'fiber': { label: 'Fiber', unit: 'g', aliases: ['dietary_fiber'] },
    'leucine': { label: 'Leucine', subLabel: 'EAA', unit: 'mg', aliases: ['l-leucine'] },
    'collagen': { label: 'Collagen', unit: 'mg', aliases: ['marine_collagen'] },
    'hyaluronic_acid': { label: 'Hyaluronic Acid', unit: 'mg', aliases: ['sodium_hyaluronate'] },
    'glutamine': { label: 'Glutamine', unit: 'mg', aliases: ['l-glutamine'] },
    'vitamin_c': { label: 'Vitamin C', subLabel: 'Ascorbic Acid', unit: 'mg', aliases: ['ascorbic_acid', 'vit_c'] },
    'glycine': { label: 'Glycine', unit: 'mg', aliases: ['l-glycine'] },
    
    // OTHER AMINOS
    'isoleucine': { label: 'Isoleucine', unit: 'mg', aliases: ['l-isoleucine'] },
    'valine': { label: 'Valine', unit: 'mg', aliases: ['l-valine'] },
    'lysine': { label: 'Lysine', unit: 'mg', aliases: ['l-lysine'] },
    'threonine': { label: 'Threonine', unit: 'mg', aliases: ['l-threonine'] },
    'phenylalanine': { label: 'Phenylalanine', unit: 'mg', aliases: ['l-phenylalanine'] },
    'methionine': { label: 'Methionine', unit: 'mg', aliases: ['l-methionine'] },
    'arginine': { label: 'Arginine', unit: 'mg', aliases: ['l-arginine'] },
    'histidine': { label: 'Histidine', unit: 'mg', aliases: ['l-histidine', 'histydyna'] },
    'tryptophan': { label: 'Tryptophan', unit: 'mg', aliases: ['l-tryptophan'] },
    'cysteine': { label: 'Cysteine', unit: 'mg', aliases: ['l-cysteine'] }
};

export interface PillarDefinition {
    id: string;
    label: string;
    color: string;
    keys: string[];
}

export const PILLARS: PillarDefinition[] = [
    { id: 'energy', label: 'ENERGY', color: '#f29b11', keys: ['creatine', 'iodine', 'zinc', 'vitamin_d', 'tyrosine', 'ashwagandha', 'caffeine'] },
    { id: 'cns', label: 'CNS', color: '#3b82f6', keys: ['magnesium', 'potassium', 'sodium', 'calcium', 'taurine', 'melatonin'] },
    { id: 'vascular', label: 'VASCULAR', color: '#ef4444', keys: ['citrulline', 'iron', 'vitamin_b12', 'omega_3', 'vitamin_e'] },
    { id: 'structural', label: 'STRUCTURAL', color: '#2ecc71', keys: ['fiber', 'leucine', 'collagen', 'hyaluronic_acid', 'glutamine', 'vitamin_c', 'glycine'] }
];

export const ALL_PILLAR_KEYS = PILLARS.flatMap(p => p.keys);

/**
 * Normalizes any nutrient key (AI or Manual) to the Canonical Registry Key.
 */
export const normalizeNutrientKey = (rawKey: string): string => {
    const key = rawKey.toLowerCase().replace(/\s+/g, '_');
    if (NUTRIENT_REGISTRY[key]) return key;
    for (const [canonical, profile] of Object.entries(NUTRIENT_REGISTRY)) {
        if (profile.aliases.includes(key)) return canonical;
    }
    return key;
};

/**
 * HAZARDOUS COMPOUNDS
 */
export const HAZARDOUS_KEYS = ['caffeine', 'sodium', 'melatonin'];
export const isHazardous = (key: string) => HAZARDOUS_KEYS.includes(normalizeNutrientKey(key));

/**
 * UNIVERSAL PROGRESS COLORS (V7.8)
 * UNIFIED: White for baseline, Green/Red for overflow.
 */
export const resolveProgressColor = (key: string, value: number, target: number): string => {
    if (!target || target <= 0) return '#e2e8f0'; 
    const isExceeded = value > target;
    const dangerous = isExceeded && isHazardous(key);
    
    if (dangerous) return '#ef4444'; // Red Alert
    if (isExceeded) return '#2ecc71'; // Unified Green for healthy overflow
    return '#e2e8f0'; // Unified White for baseline
};
